import React, { useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ArrowLeft, Upload, Camera, FileText, MapPin, Sparkles, Save, HelpCircle,
  CheckCircle2, ScanLine, Building2, FileDown, Ruler, DatabaseZap,
} from "lucide-react";
import { Layout } from "@/components/Layout";
import { TrafficLight } from "@/components/TrafficLight";
import { OverlayEngine } from "@/components/OverlayEngine";
import { SpecialistServices } from "@/components/SpecialistServices";
import { GuidedScanModal, CameraCaptureModal } from "@/components/GuidedScanModal";
import { RulerCalibration } from "@/components/RulerCalibration";
import { SignaturePad } from "@/components/SignaturePad";
import { PdfPreviewModal } from "@/components/PdfPreviewModal";
import { SendReportCard } from "@/components/SendReportCard";
import { PdfPageSelector } from "@/components/PdfPageSelector";
import { DelegaCard } from "@/components/DelegaCard";
import { ViewerErrorBoundary } from "@/components/ViewerErrorBoundary";
import api from "@/lib/api";

const CATASTO_FIELDS = [
  { key: "comune", label: "Comune", testid: "catasto-comune-input", ph: "Es. Cagliari" },
  { key: "sezione", label: "Sezione", testid: "catasto-sezione-input", ph: "—" },
  { key: "foglio", label: "Foglio", testid: "catasto-foglio-input", ph: "Es. 24" },
  { key: "particella", label: "Particella / Mappale", testid: "catasto-particella-input", ph: "Es. 412" },
  { key: "subalterno", label: "Subalterno", testid: "catasto-subalterno-input", ph: "Es. 5" },
];

const ACCEPTED = ["image/png", "image/jpeg", "image/webp", "application/pdf"];
const ACCEPTED_EXT = /\.(png|jpe?g|webp|pdf)$/i;
const isAccepted = (f) => ACCEPTED.includes(f.type) || ACCEPTED_EXT.test(f.name || "");

const UploadCard = ({ tipo, title, subtitle, colorDot, file, onUpload, onGuide, onCamera, praticaId }) => {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const handleFiles = (files) => {
    const f = files?.[0];
    if (!f) return;
    if (!isAccepted(f)) { toast.error("Formato non supportato: usa PNG, JPEG, WEBP o PDF"); return; }
    onUpload(f);
  };
  const onDrop = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); handleFiles(e.dataTransfer.files); };
  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); if (!dragOver) setDragOver(true); };
  const onDragLeave = (e) => { e.preventDefault(); if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false); };

  return (
    <div
      data-testid={`upload-${tipo}-card`}
      onDrop={onDrop} onDragOver={onDragOver} onDragEnter={onDragOver} onDragLeave={onDragLeave}
      className={`relative rounded-xl border bg-[#121824] p-5 transition-colors ${dragOver ? "border-cyan-400 ring-2 ring-cyan-500/40" : "border-slate-800"}`}
    >
      {dragOver && (
        <div data-testid={`upload-${tipo}-drop-overlay`} className="absolute inset-0 z-10 rounded-xl bg-[#0B0F17]/85 backdrop-blur-sm flex flex-col items-center justify-center pointer-events-none">
          <Upload className="h-8 w-8 text-cyan-300 mb-2" />
          <p className="text-sm font-medium text-cyan-200">Rilascia qui la planimetria</p>
          <p className="text-[11px] text-slate-400 mt-1">{title}</p>
        </div>
      )}
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="h-2.5 w-2.5 rounded-sm shrink-0" style={{ background: colorDot }} />
          <h3 data-testid={`upload-${tipo}-title`} className="font-heading font-semibold whitespace-nowrap">{title}</h3>
        </div>
        {tipo === "concessione" && (
          <button data-testid="guided-scan-modal-trigger" onClick={onGuide} className="shrink-0 inline-flex items-center gap-1.5 text-xs text-cyan-400 hover:text-cyan-300">
            <HelpCircle className="h-3.5 w-3.5" /> Guida
          </button>
        )}
      </div>
      <p className="text-xs text-slate-500 mb-4">{subtitle}</p>

      {file ? (
        <div data-testid={`upload-${tipo}-dropzone`} className="flex items-center gap-3 p-3 rounded-lg bg-[#0B0F17] border border-emerald-500/30">
          <CheckCircle2 className="h-5 w-5 text-emerald-400 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm truncate">{file.original_filename}</p>
            <p className="text-[11px] text-slate-500">{Math.round((file.size || 0) / 1024)} KB · {file.converted_from_pdf ? "PDF convertito in immagine" : "caricato"} · trascina un file per sostituire</p>
          </div>
          <span data-testid={tipo === "concessione" ? "auto-scale-100-badge" : undefined} className="font-mono-t text-[10px] px-2 py-1 rounded bg-cyan-500/10 text-cyan-300 border border-cyan-500/20">1:100</span>
        </div>
      ) : (
        <div
          data-testid={`upload-${tipo}-dropzone`}
          onClick={() => inputRef.current?.click()}
          className="border-2 border-dashed border-slate-700 rounded-lg p-6 text-center cursor-pointer hover:border-cyan-500/50 transition-colors"
        >
          <Upload className="h-6 w-6 text-slate-500 mx-auto mb-2" />
          <p className="text-sm text-slate-400">Trascina qui il file o clicca per caricare</p>
          <p className="text-[11px] text-slate-600 mt-1">PNG, JPEG, WEBP o PDF</p>
        </div>
      )}

      <input
        ref={inputRef}
        data-testid={`upload-${tipo}-input`}
        type="file"
        accept="image/png,image/jpeg,image/webp,application/pdf,.pdf,.png,.jpg,.jpeg,.webp"
        className="hidden"
        onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
      />
      {file && <PdfPageSelector praticaId={praticaId} tipo={tipo} file={file} />}

      <div className="flex gap-2 mt-3">
        <button
          data-testid={`upload-${tipo}-button`}
          onClick={() => inputRef.current?.click()}
          className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-[#1A2332] border border-slate-700 text-sm hover:border-cyan-500/50 transition-colors"
        >
          <Upload className="h-4 w-4" /> {file ? "Sostituisci" : "Carica file"}
        </button>
        {tipo === "concessione" && (
          <button
            data-testid="camera-capture-button"
            onClick={onCamera}
            className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-[#1A2332] border border-slate-700 text-sm hover:border-cyan-500/50 transition-colors"
          >
            <Camera className="h-4 w-4" /> Scatta
          </button>
        )}
      </div>
    </div>
  );
};

export default function PraticaDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [guideOpen, setGuideOpen] = useState(false);
  const [camOpen, setCamOpen] = useState(false);
  const [catasto, setCatasto] = useState(null);
  const [calibOpen, setCalibOpen] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [lookup, setLookup] = useState(null);

  const { data: pratica, isLoading } = useQuery({
    queryKey: ["pratica", id],
    queryFn: async () => (await api.get(`/pratiche/${id}`)).data,
  });

  React.useEffect(() => {
    if (pratica && catasto === null) setCatasto(pratica.catasto || {});
  }, [pratica, catasto]);

  // evita che il browser apra il PDF trascinato fuori dai riquadri
  React.useEffect(() => {
    const block = (e) => { if (e.dataTransfer?.types?.includes("Files")) e.preventDefault(); };
    window.addEventListener("dragover", block);
    window.addEventListener("drop", block);
    return () => { window.removeEventListener("dragover", block); window.removeEventListener("drop", block); };
  }, []);

  const uploadMut = useMutation({
    mutationFn: async ({ tipo, file }) => {
      const fd = new FormData();
      fd.append("tipo", tipo);
      fd.append("file", file);
      return (await api.post(`/pratiche/${id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
    },
    onSuccess: (f) => {
      qc.invalidateQueries({ queryKey: ["pratica", id] });
      toast.success(f?.converted_from_pdf ? "PDF convertito in planimetria raster (scala 1:100)" : "Planimetria caricata e ridimensionata in scala 1:100");
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore nel caricamento"),
  });

  const saveCatastoMut = useMutation({
    mutationFn: async () => (await api.put(`/pratiche/${id}`, { catasto })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["pratica", id] }); toast.success("Riferimenti catastali salvati"); },
  });

  const lookupMut = useMutation({
    mutationFn: async () => (await api.get(`/catasto/lookup`, { params: { foglio: catasto?.foglio || "", particella: catasto?.particella || "", comune: catasto?.comune || "", subalterno: catasto?.subalterno || "" } })).data,
    onSuccess: (d) => {
      setCatasto({ comune: d.comune, sezione: d.sezione, foglio: d.foglio, particella: d.particella, subalterno: d.subalterno });
      setLookup(d);
      toast.info("Riferimenti precompilati (dati dimostrativi)");
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore archivio catastale"),
  });

  const analyzeMut = useMutation({
    mutationFn: async () => (await api.post(`/pratiche/${id}/analyze`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["pratica", id] }); toast.success("Analisi completata"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore durante l'analisi"),
  });

  if (isLoading || !pratica) {
    return (
      <Layout>
        <div className="flex justify-center py-20">
          <div className="h-8 w-8 rounded-full border-2 border-cyan-500/30 border-t-cyan-400 animate-spin" />
        </div>
      </Layout>
    );
  }

  const canAnalyze = pratica.concessione_file && pratica.catastale_file;
  const analysis = pratica.analysis;
  const showServices = analysis && (analysis.traffic_light === "giallo" || analysis.traffic_light === "rosso");

  return (
    <Layout>
      <button onClick={() => navigate("/dashboard")} className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-slate-100 mb-5">
        <ArrowLeft className="h-4 w-4" /> Torna alle pratiche
      </button>

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Building2 className="h-4 w-4 text-cyan-400" />
            <span className="font-mono-t text-[11px] uppercase tracking-widest text-slate-500">Stato Legittimo · Art. 9-bis DPR 380/2001</span>
          </div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">{pratica.titolo}</h1>
          {pratica.indirizzo && <p className="flex items-center gap-1.5 text-slate-400 mt-1.5"><MapPin className="h-4 w-4" /> {pratica.indirizzo}</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            data-testid="ruler-calibration-btn"
            disabled={!pratica.concessione_file && !pratica.catastale_file}
            onClick={() => setCalibOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-3 rounded-xl bg-[#1A2332] border border-slate-700 text-sm hover:border-cyan-500/50 transition-colors disabled:opacity-40"
          >
            <Ruler className="h-4 w-4" /> Calibra righello
          </button>
          <button
            data-testid="export-pdf-report-btn"
            disabled={!analysis}
            onClick={() => setPdfOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-3 rounded-xl bg-[#1A2332] border border-slate-700 text-sm hover:border-cyan-500/50 transition-colors disabled:opacity-40"
          >
            <FileDown className="h-4 w-4" /> Report PDF
          </button>
          <button
            data-testid="calculate-discrepancy-btn"
            disabled={!canAnalyze || analyzeMut.isPending}
            onClick={() => analyzeMut.mutate()}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-cyan-500 text-slate-900 font-semibold hover:bg-cyan-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Sparkles className="h-5 w-5" />
            {analyzeMut.isPending ? "Analisi AI in corso…" : "Calcola discostamento"}
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Left column: uploads + catasto */}
        <div className="space-y-5">
          <ViewerErrorBoundary name="concessione">
          <UploadCard
            tipo="concessione"
            praticaId={pratica.id}
            title="Concessione Edilizia"
            subtitle="Titolo abilitativo. Acquisisci da scansione o fotografia seguendo la guida."
            colorDot="#FF3366"
            file={pratica.concessione_file}
            onUpload={(file) => uploadMut.mutate({ tipo: "concessione", file })}
            onGuide={() => setGuideOpen(true)}
            onCamera={() => setCamOpen(true)}
          />
          </ViewerErrorBoundary>
          <ViewerErrorBoundary name="catastale">
          <UploadCard
            tipo="catastale"
            praticaId={pratica.id}
            title="Planimetria Catastale"
            subtitle="Rappresentazione depositata in Agenzia delle Entrate (vettoriale 1:100)."
            colorDot="#FFD600"
            file={pratica.catastale_file}
            onUpload={(file) => uploadMut.mutate({ tipo: "catastale", file })}
          />
          </ViewerErrorBoundary>

          <div className="rounded-xl border border-slate-800 bg-[#121824] p-5">
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="h-4 w-4 text-cyan-400" />
              <h3 className="font-heading font-semibold">Riferimenti Catastali Unici</h3>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {CATASTO_FIELDS.map((f) => (
                <div key={f.key} className={f.key === "comune" ? "col-span-2" : ""}>
                  <label className="text-[11px] text-slate-500 uppercase tracking-wider">{f.label}</label>
                  <input
                    data-testid={f.testid}
                    value={catasto?.[f.key] || ""}
                    onChange={(e) => setCatasto({ ...catasto, [f.key]: e.target.value })}
                    placeholder={f.ph}
                    className="mt-1 w-full px-3 py-2 rounded-lg bg-[#0B0F17] border border-slate-700 focus:border-cyan-500 outline-none text-sm"
                  />
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-4">
              <button
                data-testid="fetch-catasto-button"
                onClick={() => lookupMut.mutate()}
                disabled={lookupMut.isPending || !catasto?.foglio || !catasto?.particella}
                className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-sm hover:bg-amber-500/20 transition-colors disabled:opacity-40"
              >
                <DatabaseZap className="h-4 w-4" /> {lookupMut.isPending ? "Ricerca…" : "Archivio catastale"}
              </button>
              <button
                data-testid="save-catasto-button"
                onClick={() => saveCatastoMut.mutate()}
                disabled={saveCatastoMut.isPending}
                className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-[#1A2332] border border-slate-700 text-sm hover:border-cyan-500/50 transition-colors"
              >
                <Save className="h-4 w-4" /> Salva
              </button>
            </div>
            {lookup && (
              <div data-testid="catasto-lookup-result" className="mt-3 p-3 rounded-lg bg-[#0B0F17] border border-amber-500/20 text-xs space-y-1">
                <p className="text-amber-400 font-medium">Visura dimostrativa · {lookup.codice_catastale} ({lookup.provincia})</p>
                <p className="text-slate-300">{lookup.indirizzo} · piano {lookup.piano}</p>
                <p className="text-slate-400">Cat. {lookup.categoria} · classe {lookup.classe} · {lookup.consistenza} · rendita {lookup.rendita}</p>
                <p className="text-slate-500">Ultima variazione {lookup.ultima_variazione}</p>
                <p className="text-[10px] text-slate-600 italic">{lookup.avviso}</p>
              </div>
            )}
            {pratica.calibration && (
              <div data-testid="calibration-badge" className="mt-3 font-mono-t text-[11px] text-cyan-300/80 space-y-0.5">
                {Object.entries(pratica.calibration).map(([k, v]) => (
                  <p key={k}>Righello {k}: {v.px_per_cm} px/cm · 1 cm = 1 m</p>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right columns: overlay + results */}
        <div className="lg:col-span-2 space-y-5">
          <ViewerErrorBoundary name="sovrapposizione">
            <OverlayEngine
              praticaId={pratica.id}
              concessionePath={pratica.concessione_file?.storage_path}
              catastalePath={pratica.catastale_file?.storage_path}
              alignment={pratica.alignment}
              calibration={pratica.calibration}
              quote={pratica.quote}
              regioni={pratica.analysis?.regioni}
            />
          </ViewerErrorBoundary>

          {analysis ? (
            <div className="space-y-5 fade-up">
              <TrafficLight status={analysis.traffic_light} percentage={analysis.discrepancy_percentage} />

              <div data-testid="conformity-summary-card" className="rounded-xl border border-slate-800 bg-[#121824] p-5">
                <h3 className="font-heading font-semibold flex items-center gap-2 mb-3">
                  <FileText className="h-4 w-4 text-cyan-400" /> Sintesi della verifica
                </h3>
                <p className="text-sm text-slate-300 leading-relaxed">{analysis.sintesi}</p>

                {analysis.differenze?.length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs text-rose-400 uppercase tracking-wider mb-2">Difformità rilevate</p>
                    <ul className="space-y-1.5">
                      {analysis.differenze.map((d, i) => (
                        <li key={i} className="flex gap-2 text-sm text-slate-400"><span className="text-rose-400 mt-1">•</span> {d}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {analysis.aree_conformi?.length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs text-emerald-400 uppercase tracking-wider mb-2">Aree conformi</p>
                    <ul className="space-y-1.5">
                      {analysis.aree_conformi.map((d, i) => (
                        <li key={i} className="flex gap-2 text-sm text-slate-400"><span className="text-emerald-400 mt-1">•</span> {d}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {analysis.note_normative && (
                  <div className="mt-4 p-3 rounded-lg bg-[#0B0F17] border border-slate-800">
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Note normative</p>
                    <p className="text-sm text-slate-300">{analysis.note_normative}</p>
                  </div>
                )}
              </div>

              {showServices && <SpecialistServices pratica={pratica} />}
              <SignaturePad pratica={pratica} />
              <SendReportCard pratica={pratica} />
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-slate-800 bg-[#121824]/50 p-8 text-center">
              <ScanLine className="h-8 w-8 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400">Carica entrambe le planimetrie ed esegui l'analisi</p>
              <p className="text-sm text-slate-600 mt-1">Il motore AI calcolerà il discostamento geometrico e l'esito a semaforo.</p>
            </div>
          )}
          <ViewerErrorBoundary name="delega">
            <DelegaCard key={pratica.delega?.updated_at || "new"} pratica={pratica} />
          </ViewerErrorBoundary>
        </div>
      </div>

      <ViewerErrorBoundary name="modali">
        <GuidedScanModal open={guideOpen} onOpenChange={setGuideOpen} />
        <RulerCalibration open={calibOpen} onOpenChange={setCalibOpen} pratica={pratica} />
        <PdfPreviewModal open={pdfOpen} onOpenChange={setPdfOpen} praticaId={pratica.id} titolo={pratica.titolo} />
        <CameraCaptureModal open={camOpen} onOpenChange={setCamOpen} onCapture={(file) => uploadMut.mutate({ tipo: "concessione", file })} />
      </ViewerErrorBoundary>
    </Layout>
  );
}
